import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icons from "../icons";
import TicketCoveragePanel from "./TicketCoveragePanel";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/**
 * "Create Tests" wizard launched from a ticket.
 *
 * Reads the ticket's Category custom field and resolves it to a board
 * sub-category (category names are unique across boards). When the ticket has
 * no Category — or names a sub-category that doesn't exist — the user picks any
 * board + sub-category to create under. The user then chooses AI or Manual
 * mode. On continue we ensure the ticket's local anchor exists (tied to the
 * chosen board) and hand the resolved target + anchor up via onLaunch, so the
 * caller renders the existing creation page with the ticket pre-linked.
 *
 * Shared by the Tickets page and Explore & Author's Playwright entry point.
 */
export default function CreateTestsModal({
  ticket,
  projectKey,
  onClose,
  onLaunch,
  /* Optional: discover Xray tests from a different issue than `ticket` (an
     epic), and arrive with specific tests already chosen. */
  discoverKey = "",
  initialXrayKeys = [],
  xrayUnlinked = false,
}) {
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [resolved, setResolved] = useState(null); // { subcategorykey, board_id, board_name, name }
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subKey, setSubKey] = useState("");
  const [mode, setMode] = useState(""); // "ai" | "manual"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [coverageTotals, setCoverageTotals] = useState(null);

  const ticketKeys = useMemo(() => [ticket.key], [ticket.key]);
  const onCoverageLoaded = useCallback((totals) => setCoverageTotals(totals), []);

  // On open: fetch the ticket's Category + try to resolve it, and load boards
  // for the manual-pick fallback.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [tRes, bRes] = await Promise.all([
          fetch(`${API_BASE_URL}jira/ticket?key=${encodeURIComponent(ticket.key)}`),
          fetch(`${API_BASE_URL}boards`),
        ]);
        const tBody = await tRes.json().catch(() => ({}));
        const bBody = await bRes.json().catch(() => ({}));
        if (!active) return;
        setBoards(Array.isArray(bBody?.data) ? bBody.data : []);
        const cat = (tBody?.data?.category || "").trim();
        setCategory(cat);
        if (cat) {
          const rRes = await fetch(
            `${API_BASE_URL}subcategories/resolve?name=${encodeURIComponent(cat)}`
          );
          const rBody = await rRes.json().catch(() => ({}));
          if (active && rBody?.data?.found) setResolved(rBody.data);
        }
      } catch {
        if (active) setError("Failed to load the ticket's category or test suites.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [ticket.key]);

  // Load the chosen board's sub-categories (manual-pick path only).
  useEffect(() => {
    if (resolved || !boardId) {
      setSubs([]);
      return undefined;
    }
    let active = true;
    setSubsLoading(true);
    setSubKey("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}boards/${encodeURIComponent(boardId)}/subcategories`
        );
        const body = await res.json().catch(() => ({}));
        if (active) setSubs(Array.isArray(body?.data) ? body.data : []);
      } catch {
        if (active) setSubs([]);
      } finally {
        if (active) setSubsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [resolved, boardId]);

  // A board whose categories have finished loading and has none. Such a board is
  // a complete target on its own — test cases may sit directly on a board, which
  // is what the API calls standalone (subcategorykey = null).
  const boardHasNoSubs = !!boardId && !subsLoading && subs.length === 0;

  // The final create target (board + sub-category) from whichever path applies.
  const target = useMemo(() => {
    if (resolved) {
      return {
        board: { id: resolved.board_id, name: resolved.board_name },
        subcategorykey: resolved.subcategorykey,
        subName: resolved.name,
      };
    }
    if (!boardId || subsLoading) return null;

    const b = boards.find((x) => x.board_id === boardId);
    const board = { id: boardId, name: b?.name || boardId };

    // No categories on this board: file the tests under the board itself rather
    // than blocking the user. Requiring a category here used to be a dead end —
    // there was nothing to pick, so Continue could never enable.
    if (subs.length === 0) {
      return { board, subcategorykey: null, subName: "" };
    }
    if (subKey) {
      const s = subs.find((x) => x.subcategorykey === subKey);
      return { board, subcategorykey: subKey, subName: s?.name || "" };
    }
    return null;
  }, [resolved, boardId, subKey, boards, subs, subsLoading]);

  const canContinue = !!target && !!mode && !busy && !loading;

  const handleContinue = async () => {
    if (!target) return;
    setBusy(true);
    setError("");
    try {
      // Ensure the ticket's local anchor exists and is tied to this board.
      const res = await fetch(`${API_BASE_URL}ticket-links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticket_key: ticket.key,
          project_key: projectKey || "",
          epic_key: ticket.epic_key || "",
          summary: ticket.summary || "",
          status: ticket.status || "",
          issue_type: ticket.issue_type || "",
          priority: ticket.priority || "",
          url: ticket.url || "",
          board_id: target.board.id,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.data?.ticketkey) {
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      onLaunch({
        mode,
        board: target.board,
        subcategorykey: target.subcategorykey,
        /* Carried straight through: the Xray tests may live on the epic even
           though the scripts link to this ticket. */
        discoverKey,
        initialXrayKeys,
        xrayUnlinked,
        presetTickets: [
          {
            ticketkey: body.data.ticketkey,
            ticket_key: body.data.ticket_key,
            summary: body.data.summary || ticket.summary || "",
          },
        ],
      });
    } catch (e) {
      setError(`Could not start: ${e.message}`);
      setBusy(false);
    }
  };

  const modeCard = (id, title, subtitle, Icon) => {
    const selected = mode === id;
    return (
      <button
        type="button"
        onClick={() => setMode(id)}
        className={[
          "flex flex-1 items-start gap-3 rounded-xl border p-3 text-left transition",
          selected
            ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
            : "border-slate-200 bg-white hover:bg-slate-50",
        ].join(" ")}
      >
        <span
          className={[
            "flex h-8 w-8 flex-none items-center justify-center rounded-lg",
            selected ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-500",
          ].join(" ")}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium text-slate-900">{title}</span>
          <span className="mt-0.5 block text-xs text-slate-500">{subtitle}</span>
        </span>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className={`flex ${DIALOG_HEIGHT} w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">Create tests</h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">
              <span className="font-mono font-medium text-brand-700">{ticket.key}</span>{" "}
              {ticket.summary}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Icons.Spinner className="h-4 w-4 animate-spin" />
              Reading the ticket's category…
            </div>
          ) : (
            <>
              {/* Step 1 — where the test cases land */}
              <div>
                <p className="mb-2 text-sm font-semibold text-slate-900">
                  1. Where should the tests go?
                </p>
                {resolved ? (
                  <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-3 text-sm">
                    <p className="flex items-center gap-2 font-medium text-green-800">
                      <Icons.Check className="h-4 w-4 flex-none" />
                      Category “{resolved.name}”
                    </p>
                    <p className="mt-1 text-green-700">
                      Resolved from the ticket. Tests will be created under board{" "}
                      <span className="font-semibold">{resolved.board_name}</span>.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      {category
                        ? `The ticket's Category “${category}” doesn't match any category. Pick where to create the tests.`
                        : "This ticket has no Category. Pick the test suite to create the tests under, plus a category if that test suite has any."}
                    </p>
                    <label className="block text-sm">
                      <span className="mb-1 block font-medium text-slate-700">Test Suite</span>
                      <select
                        value={boardId}
                        onChange={(e) => {
                          // Clear the old board's categories in the same update as
                          // the board itself. Leaving them until the fetch effect
                          // runs lets `target` pair the new board with the previous
                          // board's category for a frame — long enough to click
                          // Continue on a mismatched pair.
                          const next = e.target.value;
                          setBoardId(next);
                          setSubKey("");
                          setSubs([]);
                          setSubsLoading(!!next);
                        }}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">Select a test suite…</option>
                        {boards.map((b) => (
                          <option key={b.board_id} value={b.board_id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block text-sm">
                      <span className="mb-1 block font-medium text-slate-700">
                        Category
                        {boardHasNoSubs && (
                          <span className="ml-1 font-normal text-slate-400">
                            (not applicable)
                          </span>
                        )}
                      </span>
                      <select
                        value={subKey}
                        onChange={(e) => setSubKey(e.target.value)}
                        disabled={!boardId || subsLoading || boardHasNoSubs}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                      >
                        <option value="">
                          {!boardId
                            ? "Pick a test suite first…"
                            : subsLoading
                            ? "Loading categories…"
                            : subs.length
                            ? "Select a category…"
                            : "This test suite has no categories"}
                        </option>
                        {subs.map((s) => (
                          <option key={s.subcategorykey} value={s.subcategorykey}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    {/* Outside the <label> on purpose: text inside it becomes part
                        of the select's accessible name, and "…has no categories"
                        is a hint about the board, not the name of the control. */}
                    {boardHasNoSubs && (
                      <p className="flex items-start gap-1.5 text-xs text-slate-500">
                        <Icons.Check className="mt-0.5 h-3.5 w-3.5 flex-none text-green-600" />
                        <span>
                          No categories here, so the tests will be created directly
                          under{" "}
                          <span className="font-medium text-slate-700">
                            {boards.find((b) => b.board_id === boardId)?.name || boardId}
                          </span>
                          .
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Step 2 — what already exists for this ticket */}
              <div>
                <p className="mb-2 text-sm font-semibold text-slate-900">
                  2. What already exists for this ticket?
                </p>
                <TicketCoveragePanel
                  ticketKeys={ticketKeys}
                  onLoaded={onCoverageLoaded}
                  title="Existing tests"
                  defaultOpen
                />
                {coverageTotals?.automated > 0 && (
                  <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
                    {coverageTotals.automated} test
                    {coverageTotals.automated === 1 ? " already has" : "s already have"} an
                    Artifact. On the next screen those are un-ticked by
                    default, so continuing won't create them again.
                  </p>
                )}
              </div>

              {/* Step 3 — creation mode */}
              <div>
                <p className="mb-2 text-sm font-semibold text-slate-900">
                  3. How should they be created?
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  {modeCard(
                    "ai",
                    "AI",
                    "Generate multiple test cases automatically.",
                    Icons.Spark
                  )}
                  {modeCard(
                    "manual",
                    "Manual",
                    "Record one test case by hand.",
                    Icons.Pencil
                  )}
                </div>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
            </>
          )}
        </div>

        <div className="flex flex-none items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-60"
          >
            {busy && <Icons.Spinner className="h-4 w-4 animate-spin" />}
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
